/**
 * LES PAGES ANNEXES, DÉCLARÉES UNE FOIS.
 *
 * Le client a demandé que la puce « Security » de la home devienne
 * « Resources » et « renvoie vers pages annexes (même domaine) », et que ces
 * pages portent « une side bar avec pages et sous pages » (Notion, « Last push
 * before going live » → Aikka Platform → Resources). Pour qu'une barre latérale
 * puisse lister *les* pages annexes, il faut qu'il existe une liste — et une
 * seule, sinon l'index /resources, la barre latérale et le pied de page se
 * mettent à diverger comme les deux pieds de page l'avaient déjà fait
 * (voir `components/layout/legalLinks.ts`, même leçon).
 *
 * ── DEUX CHANGEMENTS DEMANDÉS DEPUIS ───────────────────────────────────────
 *
 * 1. LES DOCUMENTS VIVENT SOUS `/resources`. « tu fusionnes sous resource avec
 *    peut-être des sous pages encore après ». Les sept URL sont donc
 *    `/resources/<doc>` et non plus des routes de premier niveau. Les anciennes
 *    ne disparaissent pas pour autant : `App.tsx` les garde en redirection —
 *    ce sont des pages légales, elles ont été publiées, citées par le bandeau
 *    de consentement et indexées. Une URL légale qui renvoie 404 est un défaut,
 *    pas un déménagement.
 *
 * 2. LA BARRE EST GROUPÉE. La référence est une doc Mintlify, dont la lisibilité
 *    tient à ses intertitres en petites capitales. Trois groupes, arbitrés avec
 *    le client : Légal, Confidentialité, Sécurité. Ce n'est pas décoratif — le
 *    deuxième groupe est le seul à porter des sous-pages, et il fallait un
 *    endroit où les poser qui ne soit pas « un sixième document ».
 *
 * `id` sert deux choses : la clé i18n sous `pages:resources.pages.<id>` et le
 * marquage de la page courante dans la barre latérale (`activePage`).
 */
export type ResourcePageId =
  | 'legal'
  | 'privacy'
  | 'terms'
  | 'cookies'
  | 'security'
  | 'support'
  | 'docs'
  | 'changelog';
export type ResourceSubpageId = 'dataProtection' | 'dataUse';

/** Le préfixe commun. Écrit une fois : les routes, les redirections, le
 *  sitemap et la barre latérale le lisent tous d'ici. */
export const RESOURCES_ROOT = '/resources';

export type ResourcePage = {
  id: ResourcePageId;
  /** Route react-router — jamais une URL absolue. */
  to: string;
  /** Là où cette page répondait avant la fusion, et où elle doit redirigier. */
  legacyTo: string;
  /**
   * Les sous-pages du document. Seule Confidentialité en a : ce sont les deux
   * destinations que Paramètres → Confidentialité ouvre depuis l'application.
   * Leur contenu n'est pas écrit pour l'occasion — chacune REMONTE des sections
   * déjà rédigées de la politique (voir `DataPage.tsx`). Inventer du texte
   * juridique pour un éditeur pharma français serait la mauvaise façon de
   * fermer ces deux items.
   */
  subpages?: readonly { id: ResourceSubpageId; to: string; legacyTo: string }[];
};

/**
 * LES TROIS GROUPES DE LA BARRE.
 *
 * `id` est la clé i18n sous `pages:resources.groups.<id>`. L'ordre du tableau
 * est l'ordre de la barre, et l'ordre des pages dans un groupe est celui de la
 * lecture — pas l'ordre alphabétique, qui mettrait Cookies avant les mentions
 * légales.
 */
export type ResourceGroup = {
  id: 'legal' | 'privacy' | 'security' | 'support';
  pages: readonly ResourcePage[];
};

export const RESOURCE_GROUPS: readonly ResourceGroup[] = [
  {
    id: 'legal',
    pages: [
      { id: 'legal', to: `${RESOURCES_ROOT}/legal`, legacyTo: '/legal' },
      { id: 'terms', to: `${RESOURCES_ROOT}/terms`, legacyTo: '/terms' },
      { id: 'cookies', to: `${RESOURCES_ROOT}/cookies`, legacyTo: '/cookies' },
    ],
  },
  {
    id: 'privacy',
    pages: [
      {
        id: 'privacy',
        to: `${RESOURCES_ROOT}/privacy`,
        legacyTo: '/privacy',
        subpages: [
          {
            id: 'dataProtection',
            to: `${RESOURCES_ROOT}/privacy/data-protection`,
            legacyTo: '/privacy/data-protection',
          },
          {
            id: 'dataUse',
            to: `${RESOURCES_ROOT}/privacy/data-use`,
            legacyTo: '/privacy/data-use',
          },
        ],
      },
    ],
  },
  {
    id: 'security',
    pages: [{ id: 'security', to: `${RESOURCES_ROOT}/security`, legacyTo: '/security' }],
  },
  // Le canal de support client publié (SOC 2, preuve Vanta « Customer support
  // site »). Un groupe à part : ce n'est ni un document légal ni un engagement
  // de sécurité, c'est la porte d'entrée de qui a un problème. `/support`, que
  // personne n'a jamais publié, redirige quand même : c'est l'adresse qu'on tape.
  {
    id: 'support',
    pages: [
      { id: 'support', to: `${RESOURCES_ROOT}/support`, legacyTo: '/support' },
      // La documentation produit publiée (SOC 2, preuve Vanta « Product
      // documentation site »). Rangée sous Support : c'est la même porte, celle
      // de qui cherche comment faire, et `/docs` est l'adresse qu'on tape.
      { id: 'docs', to: `${RESOURCES_ROOT}/docs`, legacyTo: '/docs' },
      // Les notes de version publiques d’aikka — PharmaGEO a les siennes sur pharma-geo.com (SOC 2, preuve Vanta « Public change log
      // or release notes »). Rangées avec le support : c'est ce qu'un client
      // consulte pour savoir ce qui a changé. `/changelog` redirige.
      { id: 'changelog', to: `${RESOURCES_ROOT}/changelog`, legacyTo: '/changelog' },
    ],
  },
];

/**
 * Les cinq documents à plat, dans l'ordre de la barre. C'est ce que lisent
 * l'index /resources et tout ce qui n'a que faire des groupes — la liste reste
 * DÉRIVÉE, pour qu'ajouter une page à un groupe suffise.
 */
export const RESOURCE_PAGES: readonly ResourcePage[] = RESOURCE_GROUPS.flatMap((g) => g.pages);

/** Les deux sous-pages, à plat, pour les mêmes raisons. */
export const PRIVACY_SUBPAGES = RESOURCE_PAGES.flatMap((p) => p.subpages ?? []);

/**
 * Toutes les redirections à poser : les sept anciennes URL vers les sept
 * nouvelles. Dérivée elle aussi — une page qu'on ajoute sans `legacyTo` n'a
 * rien à rediriger, et une page qu'on déplace en emporte une automatiquement.
 */
export const RESOURCE_REDIRECTS: readonly { from: string; to: string }[] = [
  ...RESOURCE_PAGES.map((p) => ({ from: p.legacyTo, to: p.to })),
  ...PRIVACY_SUBPAGES.map((s) => ({ from: s.legacyTo, to: s.to })),
];
